import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface FactAssignment {
  factId: string;
  groupName: string;
}

interface CreateInconsistencyFromFactsDto {
  factAssignments: FactAssignment[];
  type: string;
  source: string;
  level: string;
  description: string;
}

export const useCreateInconsistencyFromFacts = (
  matterId: string,
  options?: { onSuccess?: () => void; onError?: (error: Error) => void },
) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreateInconsistencyFromFactsDto>({
    mutationFn: async (data) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data,
      });

      const response = await fetch(`${APIBaseChronos}/api/matter/${matterId}/inconsistency/from-facts`, fetchConfig);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to create inconsistency');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate inconsistency queries to refresh the list
      queryClient.invalidateQueries([QueryEntity.Inconsistencies, { matterId }]);
      queryClient.invalidateQueries([QueryEntity.SubsetInconsistencies, { matterId }]);
      queryClient.invalidateQueries([QueryEntity.IncludedFactsCount]);
      options?.onSuccess?.();
    },
    onError: (error) => {
      console.error('Failed to create inconsistency:', error);
      options?.onError?.(error);
    },
  });
};
